import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, User, PenLine, Lock, Mail, Building2, MapPin } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import PersonalInfoSection from './PersonalInfoSection';
import SignatureSection from './SignatureSection';
import ChangePasswordSection from './ChangePasswordSection';
import { useAuthContext } from '../../contexts/AuthContext';

interface ProfileUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  department?: string;
  position?: string;
  city?: string;
  mobile_number?: string;
  manager_id?: number;
  signature_image_path?: string;
  manager?: { id: number; first_name: string; last_name: string };
  microsoft_id?: string;
  google_id?: string;
  avatar_url?: string;
}

const Profile: React.FC = () => {
  const { t } = useTranslation('profile');
  const location = useLocation();
  const { updateUser } = useAuthContext();
  const [profile, setProfile] = useState<ProfileUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  // Re-fetch profile when returning from signature editor
  useEffect(() => {
    if ((location.state as any)?.signatureUpdated) {
      fetchProfile();
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });

      if (!res.ok) {
        if (res.status === 401) { handleUnauthorized(); return; }
        throw new Error('Failed to load profile');
      }

      const data = await res.json();
      setProfile(data.user);
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdated = (user: ProfileUser) => {
    setProfile(user);
    // Update AuthContext so navbar re-renders with new name/initials
    updateUser(user as any);
  };

  const handleSignatureUpdated = (path: string) => {
    if (profile) {
      setProfile({ ...profile, signature_image_path: path });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{error || t('not_found')}</p>
      </div>
    );
  }

  const initials = `${profile.first_name?.charAt(0) || ''}${profile.last_name?.charAt(0) || ''}`.toUpperCase();

  return (
    <div className="container mx-auto max-w-3xl space-y-6">
      {/* Profile Header Banner */}
      <div className="relative rounded-xl overflow-hidden shadow-lg">
        <div className="h-32 bg-primary" />
        <div className="bg-white px-6 pb-5 pt-0 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 -mt-12">
            <Avatar className="h-24 w-24 border-4 border-white shadow-md">
              {profile.avatar_url && <AvatarImage src={`${API_BASE_URL}/users/${profile.id}/avatar`} alt={`${profile.first_name} ${profile.last_name}`} />}
              <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="pb-1 flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                {profile.first_name} {profile.last_name}
              </h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" /> {profile.email}
                </span>
                {profile.position && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3.5 w-3.5" /> {profile.position}
                  </span>
                )}
                {profile.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {profile.city}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="personal" className="w-full">
        <TabsList className={`grid w-full h-12 ${(profile.microsoft_id || profile.google_id) ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <User className="h-4 w-4" /> {t('personal_info')}
          </TabsTrigger>
          <TabsTrigger value="signature" className="flex items-center gap-2">
            <PenLine className="h-4 w-4" /> {t('signature')}
          </TabsTrigger>
          {!(profile.microsoft_id || profile.google_id) && (
            <TabsTrigger value="password" className="flex items-center gap-2">
              <Lock className="h-4 w-4" /> {t('password')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="personal">
          <PersonalInfoSection profile={profile} onProfileUpdated={handleProfileUpdated} />
        </TabsContent>

        <TabsContent value="signature">
          <SignatureSection
            userId={profile.id}
            firstName={profile.first_name}
            lastName={profile.last_name}
            signaturePath={profile.signature_image_path}
            onSignatureUpdated={handleSignatureUpdated}
          />
        </TabsContent>

        {!(profile.microsoft_id || profile.google_id) && (
          <TabsContent value="password">
            <ChangePasswordSection />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Profile;
